import { CommonModule } from '@angular/common';
import { Component, inject, input, computed } from '@angular/core';
import { BaseAssetTypeDetails, InfrastructureDetails, PortfolioCompanyDetails, RealEstateDetails } from 'src/app/models/classes/asset-dashboard.class';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { DateFormatPipe } from 'src/app/pipes/date-format.pipe';
import { PercentageValueFormatPipe } from 'src/app/pipes/percentage-format.pipe';
import { StandardStringFormatPipe } from 'src/app/pipes/standard-string-format.pipe';
import { TvpiValueFormatPipe } from 'src/app/pipes/tvpi-value-format.pipe';
import { ToNumberPipe } from 'src/app/pipes/to-number.pipe';
import { ToStringPipe } from 'src/app/pipes/to-string.pipe';
import { ConstantQueryService } from 'src/app/services/constant-query.service';

// Financial field interfaces
interface FinancialField {
  label: string;
  value: string | number | null;
  type: 'currency' | 'percentage' | 'text' | 'number';
  icon: string;
  dataCy: string;
}

@Component({
    selector: 'app-financial-summary',
    imports: [
        CommonModule,
        CurrencyFormatPipe,
        StandardStringFormatPipe,
        PercentageValueFormatPipe,
        DateFormatPipe,
        TvpiValueFormatPipe,
        ToNumberPipe,
        ToStringPipe
    ],
    templateUrl: './financial-summary.component.html',
    styleUrls: ['./financial-summary.component.scss']
})
export class FinancialSummaryComponent {
  private constants = inject(ConstantQueryService);

  readonly iconSet = input<Record<string, string> | undefined>(undefined);
  readonly assetType = input<string | undefined>(undefined);
  readonly selectedAsOfDate = input<string | undefined>(undefined);
  readonly financialData = input.required<BaseAssetTypeDetails>();
  readonly showWidget = computed(() => {
    const financialData = this.financialData();
    if (!financialData) return true;
    const filteredFinancialData = Object.keys(financialData)
      .filter(key => this.constants.financialValues.includes(key))
      .reduce((obj: Record<string, unknown>, key: string) => {
        obj[key] = financialData[key as keyof BaseAssetTypeDetails];
        return obj;
      }, {});
    return Object.values(filteredFinancialData).filter(val => val !== undefined && val !== null).length > 0;
  });

  // Computed property for financial fields based on asset type
  readonly financialFields = computed<FinancialField[]>(() => {
    const assetType = this.assetType();
    const financialData = this.financialData();
    const iconSet = this.iconSet();
    
    if (!assetType || !financialData || !iconSet) {
      return [];
    }

    switch (assetType) {
      case 'Infrastructure':
        return this.getInfrastructureFields(financialData as InfrastructureDetails, iconSet);
      
      case 'Real Estate':
        return this.getRealEstateFields(financialData as RealEstateDetails, iconSet);

      case 'Portfolio Company':
        return this.getPortfolioCompanyFields(financialData as PortfolioCompanyDetails, iconSet);

      default: return [];
    }
  });

  private getInfrastructureFields(asset: InfrastructureDetails, iconSet: Record<string, string>): FinancialField[] {
    return [
      { label: 'Enterprise Value', value: asset.value, type: 'currency', icon: iconSet['icon.enterprise.value'], dataCy: 'balanceValue' },
      { label: 'Revenue', value: asset.revenue, type: 'currency', icon: iconSet['icon.revenue'], dataCy: 'balanceValue' },
      { label: 'EBITDA', value: asset.ebitda, type: 'currency', icon: iconSet['icon.ebitda'], dataCy: 'balanceValue' },
      { label: 'Net Debt', value: asset.netDebt, type: 'currency', icon: iconSet['icon.net.debt'], dataCy: 'balanceValue' },
      { label: 'Net Leverage', value: asset.netLeverage, type: 'number', icon: iconSet['icon.net.leverage'], dataCy: 'leverageValue' },
      { label: 'Risk Profile', value: asset.riskProfile, type: 'text', icon: iconSet['icon.risk.profile'], dataCy: 'stringFormatValue' }
    ];
  }

  private getRealEstateFields(asset: RealEstateDetails, iconSet: Record<string, string>): FinancialField[] {
    return [
      { label: 'Acquisition Price', value: asset.acquisitionPrice, type: 'currency', icon: iconSet['icon.re.acq.price'], dataCy: 'balanceValue' },
      { label: 'Entry Loan to Value', value: asset.entryLoanToValue, type: 'percentage', icon: iconSet['icon.re.entry.loan.tovalue'], dataCy: 'percentageValue' },
      { label: 'Underwritten Disposition Price', value: asset.underwrittenDispositionPrice, type: 'currency', icon: iconSet['icon.re.uw.disp.price'], dataCy: 'balanceValue' },
      { label: 'Underwritten Hold Term', value: asset.underwrittenHoldTermValue, type: 'text', icon: iconSet['icon.re.uw.hold.term'], dataCy: 'stringFormatValue' }
    ];
  }

  private getPortfolioCompanyFields(asset: PortfolioCompanyDetails, iconSet: Record<string, string>): FinancialField[] {
    return [
      { label: 'Market Cap', value: asset.marketCap, type: 'currency', icon: iconSet['icon.market.cap'], dataCy: 'balanceValue' },
      { label: 'Enterprise Value', value: asset.value, type: 'currency', icon: iconSet['icon.enterprise.value'], dataCy: 'balanceValue' },
      { label: 'Revenue', value: asset.revenue, type: 'currency', icon: iconSet['icon.revenue'], dataCy: 'balanceValue' },
      { label: 'EBITDA', value: asset.ebitda, type: 'currency', icon: iconSet['icon.ebitda'], dataCy: 'balanceValue' },
      { label: 'Net Debt', value: asset.netDebt, type: 'currency', icon: iconSet['icon.net.debt'], dataCy: 'balanceValue' },
      { label: 'Net Leverage', value: asset.netLeverage, type: 'number', icon: iconSet['icon.net.leverage'], dataCy: 'leverageValue' }
    ];
  }

  getContainerDataCy(): string {
    const assetType = this.assetType();
    switch (assetType) {
      case 'Infrastructure':
      case 'Portfolio Company':
        return 'portfolioAndInfrastructureContainer';
      case 'Real Estate':
        return 'realEstateContainer';
      default:
        return 'financialContainer';
    }
  }

  isLargeLeftTextAligned(field: FinancialField): boolean {
    return field.label === 'Risk Profile' || field.label === 'Underwritten Hold Term';
  }
}
